import React, { useState, useEffect, useId, useRef } from 'react';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';

interface AutoCompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suggestions: string[];
  className?: string;
  /** Associate an external <label htmlFor> with the input. */
  id?: string;
  'aria-label'?: string;
}

export const AutoCompleteInput: React.FC<AutoCompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  suggestions,
  className,
  id,
  'aria-label': ariaLabel,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const listOpen = showSuggestions && suggestions.length > 0;
  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const suggestionsKey = suggestions.join('\n');

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestionsKey]);

  const closeList = () => {
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Claim Escape above the hosting panel while the list is open so the first
  // press closes only the list and the next press reaches the panel.
  useModalEscape('autocomplete-suggestions', closeList, {
    priority: 400,
    enabled: listOpen,
  });

  const handleSelect = (suggestion: string) => {
    const activeTokenStart = value.lastIndexOf(',');
    onChange(activeTokenStart < 0
      ? suggestion
      : `${value.slice(0, activeTokenStart + 1)} ${suggestion}`);
    closeList();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setShowSuggestions(true);
      setActiveIndex((current) => (listOpen ? (current + 1) % suggestions.length : 0));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setShowSuggestions(true);
      setActiveIndex((current) => (listOpen && current > 0 ? current - 1 : suggestions.length - 1));
      return;
    }
    if (event.key === 'Enter' && listOpen && activeIndex >= 0) {
      event.preventDefault();
      handleSelect(suggestions[activeIndex]);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listOpen ? listboxId : undefined}
        aria-activedescendant={listOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={closeList}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          "w-full px-3 py-2 rounded-md border",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          theme.focusRing,
          className
        )}
      />
      {listOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-label={t('Suggestions')}
          className={cn(
            "absolute top-full mt-1 w-full rounded-md shadow-lg z-10 max-h-40 overflow-y-auto border",
            theme.bgSecondary,
            theme.borderPrimary
          )}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(suggestion)}
              className={cn(
                "px-3 py-2 cursor-pointer",
                theme.textPrimary,
                index === activeIndex ? theme.surfaceSelected : theme.hover
              )}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 
